import { FC, useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ArrowBackIcon } from '../../../../../components/Icons/ArrowBackIcon';
import { CloseIcon } from '../../../../../components/Icons/CloseIcon';

import styles from './GoNowPanel.module.scss';
import { TripStop } from '../../../../../types/trip.types';
import { useGeolocation } from '../../../../../hooks/useGeolocation';
import {
  useRecommendLeaveTimeMutation,
  useGetTripStopGoNowReminderQuery,
  useDeleteGoNowReminderMutation,
} from '../../../../../store/services/tripsApi';
import ArrivalTimePicker from '../../../../../common/ArrivalTimePicker';
import GooglePlacesInput from '../../../../../common/GooglePlacesInput';
import SetReminderModal from '../SetReminderModal/SetReminderModal';

interface GoNowPanelProps {
  activity: TripStop;
  onClose: () => void;
  onSuccess?: () => void;
}

const GoNowPanel: FC<GoNowPanelProps> = ({ activity, onClose, onSuccess }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [customAddress, setCustomAddress] = useState('');
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);
  const [recommendedLeaveTime, setRecommendedLeaveTime] = useState('');

  const [showSetReminderModal, setShowSetReminderModal] = useState(false);
  const [modalApiPayload, setModalApiPayload] = useState<any>(null);

  const {
    location,
    isLoading: isLocationLoading,
    error: locationError,
    getCurrentLocation,
    clearError: clearLocationError,
  } = useGeolocation();

  const [recommendLeaveTime, { isLoading: isRecommending }] = useRecommendLeaveTimeMutation();

  const {
    data: existingReminder,
    refetch: refetchReminder,
    error: reminderError,
  } = useGetTripStopGoNowReminderQuery(activity.id.toString());

  const [deleteGoNowReminder, { isLoading: isDeleting }] = useDeleteGoNowReminderMutation();
  useEffect(() => {
    initializeDefaultValues();
  }, [activity]);

  const initializeDefaultValues = () => {
    const { date, time } = getDefaultArrivalDateTime();

    setSelectedDate(date);
    setSelectedTime(time);

    setUseCurrentLocation(false);
    setCustomAddress('');
    setSelectedPlace(null);
    setRecommendedLeaveTime('');
    clearLocationError();
  };

  const getDefaultArrivalDateTime = (): { date: string; time: string } => {
    let activityTime = '';
    switch (activity.stop_type) {
      case 'Flight':
        activityTime = (activity as any).departure_date_time;
        break;
      case 'Hotel':
        activityTime = (activity as any).check_in_date_time;
        break;
      default:
        activityTime = (activity as any).start_date_time;
        break;
    }

    if (activityTime) {
      const date = new Date(activityTime);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return {
        date: `${year}-${month}-${day}`,
        time: `${hours}:${minutes}`,
      };
    }

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    return {
      date: `${year}-${month}-${day}`,
      time: '12:00',
    };
  };

  const getActivityDetails = () => {
    switch (activity.stop_type) {
      case 'Hotel':
        return {
          name: (activity as any).name || 'Hotel',
          address: (activity as any).address?.name || '',
          time: `Check-in ${formatDateTime((activity as any).check_in_date_time)}`,
        };
      case 'Flight':
        return {
          name: `${(activity as any).flight_from} - ${(activity as any).flight_to}`,
          address: (activity as any).airline || '',
          time: `Departure ${formatDateTime((activity as any).departure_date_time)}`,
        };
      default:
        return {
          name: (activity as any).name || (activity as any).place || activity.stop_type,
          address: (activity as any).address?.name || '',
          time: `Start ${formatDateTime((activity as any).start_date_time)}`,
        };
    }
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return (
      date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }) +
      ', from ' +
      date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    );
  };

  const handleTimeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;

    setSelectedTime(newTime);

    const hasValidLocation = useCurrentLocation
      ? location && location.latitude && location.longitude
      : selectedPlace && selectedPlace.geometry?.location;

    if (newTime && selectedDate && hasValidLocation) {
      fetchRecommendation();
    } else {
      console.log('Time change - not calling fetchRecommendation');
    }
  };

  const handleAddressChange = async (address: string, placeDetails?: google.maps.places.PlaceResult) => {
    setCustomAddress(address);
    setSelectedPlace(placeDetails || null);

    if (placeDetails && placeDetails.geometry?.location && selectedDate && selectedTime) {
      fetchRecommendationWithPlace(placeDetails);
    } else if (placeDetails) {
      console.log('PlaceDetails found but no geometry/coordinates');
    } else {
      setRecommendedLeaveTime('');
    }
  };

  const getFullDateTime = (): string => {
    if (!selectedDate || !selectedTime) {
      return '';
    }

    const dateTimeString = `${selectedDate}T${selectedTime}:00`;
    return dateTimeString;
  };

  const handleLocationToggle = async () => {
    const newUseCurrentLocation = !useCurrentLocation;

    setUseCurrentLocation(newUseCurrentLocation);

    if (newUseCurrentLocation) {
      try {
        const currentLocation = await getCurrentLocation();

        if (selectedDate && selectedTime && currentLocation && currentLocation.latitude && currentLocation.longitude) {
          fetchRecommendationWithCurrentLocation(currentLocation);
        } else {
          console.log('Location toggle - missing data for API call');
        }
      } catch (error) {
        console.error('Failed to get current location:', error);
      }
    } else {
      setCustomAddress('');
      setSelectedPlace(null);
      setRecommendedLeaveTime('');
    }
  };

  const fetchRecommendationWithCurrentLocation = async (currentLocation: any) => {
    try {
      const fullDateTime = getFullDateTime();
      if (!fullDateTime) {
        return;
      }

      const fromAddress = {
        name: currentLocation.address || `${currentLocation.latitude}, ${currentLocation.longitude}`,
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
      };

      const apiPayload = {
        address_from: fromAddress,
        arrival_date_time: new Date(fullDateTime).toISOString(),
      };

      const result = await recommendLeaveTime({
        tripStopId: activity.id.toString(),
        payload: apiPayload,
      }).unwrap();

      setRecommendedLeaveTime(result.recommended_to_leave_by_time);
    } catch (error) {}
  };

  const fetchRecommendationWithPlace = async (placeDetails: google.maps.places.PlaceResult) => {
    try {
      const fullDateTime = getFullDateTime();
      if (!fullDateTime) {
        return;
      }

      if (!placeDetails.geometry?.location) {
        return;
      }

      const fromAddress = {
        name: placeDetails.formatted_address || customAddress,
        latitude: placeDetails.geometry.location.lat(),
        longitude: placeDetails.geometry.location.lng(),
      };

      const apiPayload = {
        address_from: fromAddress,
        arrival_date_time: new Date(fullDateTime).toISOString(),
      };

      const result = await recommendLeaveTime({
        tripStopId: activity.id.toString(),
        payload: apiPayload,
      }).unwrap();

      setRecommendedLeaveTime(result.recommended_to_leave_by_time);
    } catch (error) {
      console.error('fetchRecommendationWithPlace error:', error);
    }
  };

  const fetchRecommendation = async () => {
    try {
      const fullDateTime = getFullDateTime();
      if (!fullDateTime) {
        return;
      }

      const fromAddress =
        useCurrentLocation && location
          ? {
              name: location.address || `${location.latitude}, ${location.longitude}`,
              latitude: location.latitude,
              longitude: location.longitude,
            }
          : selectedPlace && selectedPlace.geometry?.location
            ? {
                name: selectedPlace.formatted_address || customAddress,
                latitude: selectedPlace.geometry.location.lat(),
                longitude: selectedPlace.geometry.location.lng(),
              }
            : null;

      if (!fromAddress) {
        return;
      }

      if (
        !fromAddress.latitude ||
        !fromAddress.longitude ||
        fromAddress.latitude === 0 ||
        fromAddress.longitude === 0
      ) {
        return;
      }

      const apiPayload = {
        address_from: fromAddress,
        arrival_date_time: new Date(fullDateTime).toISOString(),
      };

      const result = await recommendLeaveTime({
        tripStopId: activity.id.toString(),
        payload: apiPayload,
      }).unwrap();

      setRecommendedLeaveTime(result.recommended_to_leave_by_time);
    } catch (error) {}
  };

  const handleSetReminder = async () => {
    const fullDateTime = getFullDateTime();

    if (!fullDateTime || !recommendedLeaveTime) {
      return;
    }

    try {
      const fromAddress =
        useCurrentLocation && location
          ? {
              name: location.address || `${location.latitude}, ${location.longitude}`,
              latitude: location.latitude,
              longitude: location.longitude,
            }
          : selectedPlace && selectedPlace.geometry?.location
            ? {
                name: selectedPlace.formatted_address || customAddress,
                latitude: selectedPlace.geometry.location.lat(),
                longitude: selectedPlace.geometry.location.lng(),
              }
            : {
                name: customAddress,
                latitude: 0,
                longitude: 0,
              };

      const apiPayload = {
        arrival_date_time: new Date(fullDateTime).toISOString(),
        departure_date_time: new Date(recommendedLeaveTime).toISOString(),
        location_from_address: fromAddress,
      };

      setModalApiPayload(apiPayload);
      setShowSetReminderModal(true);
    } catch (error) {
      console.error('Error preparing reminder data:', error);
    }
  };

  const handleCancelReminder = async () => {
    if (!hasValidExistingReminder) {
      return;
    }

    try {
      await deleteGoNowReminder(existingReminder.id.toString()).unwrap();
      console.log('Reminder cancelled successfully');
      refetchReminder();
      setRecommendedLeaveTime('');

      onSuccess?.();
    } catch (error) {
      console.error('Error cancelling reminder:', error);
    }
  };

  const handleModalSuccess = () => {
    console.log('Reminder set successfully from modal');
    setShowSetReminderModal(false);
    setModalApiPayload(null);
    refetchReminder();

    onSuccess?.();
  };

  const handleModalClose = () => {
    setShowSetReminderModal(false);
    setModalApiPayload(null);
  };

  const activityDetails = getActivityDetails();

  const hasValidLocation = useCurrentLocation
    ? location && location.latitude && location.longitude
    : selectedPlace && selectedPlace.geometry?.location;

  const hasNoReminder = reminderError && 'status' in reminderError && reminderError.status === 404;

  const hasValidExistingReminder = existingReminder && existingReminder.departure_date_time && !hasNoReminder;

  const canSetReminder =
    selectedDate && selectedTime && recommendedLeaveTime && hasValidLocation && !hasValidExistingReminder;
  const canCancelReminder = hasValidExistingReminder;

  return (
    <Box className={styles.goNowPanel}>
      <Box className={styles.header}>
        <Box className={styles.headerLeft}>
          <IconButton
            onClick={onClose}
            className={styles.backButton}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h7Bold"
            className={styles.title}
          >
            Go Reminder
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          className={styles.closeButton}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={styles.content}>
        <Box className={styles.section}>
          <Typography
            variant="h9Bold"
            className={styles.sectionTitle}
            color="#222B3A"
          >
            Location Details
          </Typography>

          <Box className={styles.locationDetails}>
            <Typography
              variant="h10Bold"
              className={styles.locationName}
            >
              {activityDetails.name}
            </Typography>
            {activityDetails.address && (
              <Typography
                variant="h11"
                className={styles.locationAddress}
              >
                Address: {activityDetails.address}
              </Typography>
            )}
            <Typography
              variant="h11"
              className={styles.locationTime}
            >
              {activityDetails.time}
            </Typography>
          </Box>
        </Box>

        <Box className={styles.section}>
          <Typography
            variant="h9Bold"
            className={styles.sectionTitle}
            color="#222B3A"
          >
            Set Reminder
          </Typography>

          <Box className={styles.formField}>
            <ArrivalTimePicker
              inputLabel="When would you like to arrive?"
              value={selectedTime}
              onChange={handleTimeChange}
              placeholder="Select time"
              name="arrivalTime"
            />
          </Box>

          <Box className={styles.formField}>
            <Typography
              variant="h11"
              className={styles.fieldLabel}
            >
              What is your starting location?
            </Typography>
            {!useCurrentLocation && (
              <Box>
                <GooglePlacesInput
                  inputLabel=""
                  value={customAddress}
                  onChange={handleAddressChange}
                  placeholder="Enter your starting address..."
                />

                {customAddress && !selectedPlace && (
                  <Typography
                    variant="h12"
                    component="p"
                    className={styles.addressHint}
                  >
                    Select an address from the suggestions for accurate directions.
                  </Typography>
                )}
              </Box>
            )}

            <Box className={styles.locationToggle}>
              <Box
                className={`${styles.toggleSwitch} ${useCurrentLocation ? styles.active : ''}`}
                onClick={handleLocationToggle}
              >
                <Box className={`${styles.toggleKnob} ${useCurrentLocation ? styles.active : ''}`} />
              </Box>
              <Typography
                variant="h11"
                className={styles.toggleLabel}
                onClick={handleLocationToggle}
              >
                Use my current location
              </Typography>
            </Box>

            {useCurrentLocation && isLocationLoading && (
              <Typography
                variant="h11"
                className={styles.loadingState}
              >
                Getting your location...
              </Typography>
            )}

            {locationError && (
              <Typography
                variant="h11"
                className={styles.errorState}
              >
                {locationError.message}
              </Typography>
            )}

            {useCurrentLocation && location && (
              <Typography
                variant="h11"
                className={styles.currentLocation}
              >
                📍 {location.address || `${location.latitude}, ${location.longitude}`}
              </Typography>
            )}
          </Box>

          {(recommendedLeaveTime || hasValidExistingReminder) && (
            <Box className={styles.recommendedTime}>
              <Box className={styles.recommendedHeader}>
                <Typography
                  variant="h9Semibold"
                  className={styles.recommendedLabel}
                  color="#222B3A"
                >
                  Recommended to leave by
                </Typography>
                <Typography
                  variant="h10Semibold"
                  className={styles.recommendedTimeValue}
                >
                  {recommendedLeaveTime
                    ? new Date(recommendedLeaveTime).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })
                    : hasValidExistingReminder
                      ? new Date(existingReminder.departure_date_time).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })
                      : ''}
                </Typography>
              </Box>
              <Typography
                variant="h11"
                component="p"
                className={styles.recommendedNote}
              >
                This is a recommended departure time only. Double-check directions and traffic before you leave.
              </Typography>

              <Box className={styles.actions}>
                {canSetReminder && (
                  <button
                    onClick={handleSetReminder}
                    className={styles.primaryButton}
                  >
                    Set Reminder
                  </button>
                )}

                {canCancelReminder && (
                  <button
                    onClick={handleCancelReminder}
                    disabled={isDeleting}
                    className={styles.primaryButton}
                  >
                    {isDeleting ? 'Cancelling...' : 'Cancel Reminder'}
                  </button>
                )}
              </Box>
            </Box>
          )}

          {isRecommending && (
            <Box className={styles.loadingState}>
              <Typography variant="h11">Calculating recommended time...</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {showSetReminderModal && modalApiPayload && (
        <SetReminderModal
          open={showSetReminderModal}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          initialTime={selectedTime}
          tripStopId={activity.id.toString()}
          selectedDate={selectedDate}
          apiPayload={modalApiPayload}
        />
      )}
    </Box>
  );
};

export default GoNowPanel;
